/**
 * Utility functions for handling callback URL redirects
 */

import {
  getCallbackUrl,
  clearCallbackUrlOnly,
  isDeepLinkProcessing,
  isMobileCallbackFlow,
  clearMobileCallbackFlow,
  clearMobileCallbackSession
} from './deepLinkUtils';

/**
 * Check and handle callback URL redirect
 * This should be called when the user returns to the application
 * (e.g., after Stripe checkout completion)
 */
export const handleCallbackRedirect = (): boolean => {
  const callbackUrl = getCallbackUrl();
  
  if (callbackUrl) {
    if (isMobileCallbackFlow()) {
      const clearSessionOnExit = () => {
        clearMobileCallbackSession();
        window.removeEventListener('pagehide', clearSessionOnExit);
      };

      window.addEventListener('pagehide', clearSessionOnExit, { once: true });
    } else {
      clearMobileCallbackFlow();
      clearCallbackUrlOnly();
    }
    
    // Use window.location.href for external URLs (like app deep links)
    if (callbackUrl.startsWith('http') || callbackUrl.includes('://')) {
      window.location.replace(callbackUrl);
    } else {
      // For relative URLs, you might want to use React Router navigation
      // For now, we'll use window.location for consistency
      window.location.replace(callbackUrl);
    }
    
    return true; // Indicates that a redirect was performed
  }
  
  return false; // No callback URL found
};

/**
 * Check if we should handle callback redirect on page load
 * This can be used in useEffect hooks to handle redirects
 */
export const checkAndHandleCallback = (): boolean => {
  // Don't handle callback if we're in deep link processing mode
  if (isDeepLinkProcessing()) {
    console.log('CallbackHandler: Skipping callback redirect - deep link processing in progress');
    return false;
  }
  
  // Check if we have a callback URL stored
  const callbackUrl = getCallbackUrl();
  if (!callbackUrl) {
    return false;
  }
  
  // Check if this is a Stripe return
  const isStripeReturn = window.location.search.includes('session_id') || 
                        window.location.search.includes('payment_intent');
  
  if (isStripeReturn) {
    console.log('CallbackHandler: User returned from Stripe, redirecting to callback URL');
    return handleCallbackRedirect();
  }
  
  // For non-Stripe returns, also handle callback redirect
  console.log('CallbackHandler: Handling callback redirect for non-Stripe return');
  return handleCallbackRedirect();
};
